import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { trpc } from '@/api/trpc';
import { showToast } from '@/components/Layout';
import { Button, Callout, FormInput, Modal } from '@/components/UI';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import { useAppQuery } from '@/hooks/useAppQuery';
import {
    buildGridAxis,
    describeGridTarget,
    parseGridValues,
    suggestGridValuesExample,
    toGridValue,
    type IdeaLabGridAxis,
    type IdeaLabGridResult,
    type IdeaLabVariantDetail
} from '../ideaLabUtils';

interface VariantGridModalProps {
    variant: IdeaLabVariantDetail;
    isOpen: boolean;
    onClose: () => void;
}

const GRID_RESULT_TONES: Record<IdeaLabGridResult['children'][number]['status'], string> = {
    created: 'text-emerald-300',
    existing: 'text-zinc-400',
    rejected: 'text-red-300'
};

/**
 * Parameter sweep: expands this variant into children that each try other values for its rule
 * settings (condition values, feature selectors, and the thresholds, step windows, and selectors of
 * imported rule expressions, as the backend lists them). Every child is a frozen variation of this
 * variant, so a run shows the entries each value adds or removes. The preview lists exactly what
 * will be created before anything is.
 */
const VariantGridModal = ({ variant, isOpen, onClose }: VariantGridModalProps) => {
    const queryClient = useQueryClient();
    const targetsQuery = useAppQuery(
        {
            ...trpc.ideaLab.listVariantGridTargets.queryOptions({ variantId: variant.id }),
            enabled: isOpen
        },
        { fallbackErrorMessage: 'Failed to load the settings a sweep can vary' }
    );
    const targets = useMemo(() => targetsQuery.data ?? [], [targetsQuery.data]);
    const [inputs, setInputs] = useState<Record<string, string>>({});
    const [previewAxes, setPreviewAxes] = useState<IdeaLabGridAxis[] | null>(null);
    const [result, setResult] = useState<IdeaLabGridResult | null>(null);

    const parsed = useMemo(
        () =>
            targets.map((target) => {
                const text = inputs[target.key]?.trim() ?? '';
                return { target, text, parsed: text === '' ? null : parseGridValues(text, toGridValue(target.current)) };
            }),
        [inputs, targets]
    );
    const inputError = parsed.find((row) => row.parsed !== null && 'error' in row.parsed);
    const axes = useMemo(
        () =>
            parsed.flatMap((row) =>
                row.parsed !== null && 'values' in row.parsed && row.parsed.values.length > 0
                    ? [buildGridAxis(row.target, row.parsed.values)]
                    : []
            ),
        [parsed]
    );
    const previewIsCurrent = inputError === undefined && previewAxes !== null && JSON.stringify(previewAxes) === JSON.stringify(axes);

    const previewQuery = useAppQuery(
        {
            ...trpc.ideaLab.previewVariantGrid.queryOptions({ baseVariantId: variant.id, axes: previewAxes ?? [] }),
            enabled: isOpen && previewAxes !== null && previewAxes.length > 0
        },
        { fallbackErrorMessage: 'Failed to preview the parameter sweep' }
    );
    const preview = previewIsCurrent ? (previewQuery.data ?? null) : null;

    const createMutation = useMutation({
        ...trpc.ideaLab.createVariantGrid.mutationOptions(),
        onError: (error) => showToast(error.message, 'error'),
        onSuccess: async (created) => {
            const createdCount = created.children.filter((child) => child.status === 'created').length;
            showToast(`Created ${createdCount} sweep variant${createdCount === 1 ? '' : 's'}`, 'success');
            setResult(created);
            await queryClient.invalidateQueries(trpc.ideaLab.getStudy.pathFilter());
        }
    });

    const close = () => {
        setInputs({});
        setPreviewAxes(null);
        setResult(null);
        onClose();
    };

    return (
        <Modal
            footer={
                <div className="flex items-center justify-between gap-3">
                    <span className="text-sm text-amber-300">
                        {result ? '' : inputError && inputError.parsed && 'error' in inputError.parsed ? inputError.parsed.error : ''}
                    </span>
                    <div className="flex gap-2">
                        <Button variant="secondary" onClick={close}>
                            {result ? 'Close' : 'Cancel'}
                        </Button>
                        {!result && (
                            <Button
                                disabled={axes.length === 0 || inputError !== undefined}
                                isLoading={previewQuery.isFetching}
                                variant="secondary"
                                onClick={() => setPreviewAxes(axes)}
                            >
                                Preview
                            </Button>
                        )}
                        {!result && (
                            <Button
                                disabled={inputError !== undefined || !preview || preview.newChildCount === 0 || createMutation.isPending}
                                isLoading={createMutation.isPending}
                                variant="primary"
                                onClick={() => createMutation.mutate({ baseVariantId: variant.id, axes })}
                            >
                                {preview ? `Create ${preview.newChildCount} variant${preview.newChildCount === 1 ? '' : 's'}` : 'Create'}
                            </Button>
                        )}
                    </div>
                </div>
            }
            isOpen={isOpen}
            size="lg"
            title={`Parameter sweep from "${variant.label}"`}
            onClose={close}
        >
            {result ? (
                <div className="space-y-3">
                    <Callout variant="success">
                        Each new variant is frozen and ready to run. They are variations of this variant, so a run shows what each value
                        adds or removes.
                    </Callout>
                    <ul className="space-y-1 text-sm">
                        {result.children.map((child) => (
                            <li key={child.variantKey}>
                                <span className={`mr-2 text-xs uppercase ${GRID_RESULT_TONES[child.status]}`}>{child.status}</span>
                                {child.variantId !== null ? (
                                    <Link className="text-sky-300 hover:underline" to={`/user/ideaLab/variant/${child.variantId}`}>
                                        {child.label}
                                    </Link>
                                ) : (
                                    child.label
                                )}
                                {child.status === 'rejected' && <span className="ml-2 text-xs text-red-300">{child.message}</span>}
                            </li>
                        ))}
                    </ul>
                </div>
            ) : targetsQuery.isLoading ? (
                <DataLoadingSpinner text="Loading the settings a sweep can vary..." />
            ) : targetsQuery.error instanceof Error ? (
                <Callout variant="error">{targetsQuery.error.message}</Callout>
            ) : targets.length === 0 ? (
                <Callout variant="info">
                    This variant has no rule values, feature selectors, or rule-expression settings a sweep can vary.
                </Callout>
            ) : (
                <div className="space-y-4">
                    <Callout variant="info">
                        List the values to try for up to four settings. Every combination except this variant&apos;s own becomes a frozen
                        child, at most 24 per sweep. Separate values with commas; for a range, separate each pair with a semicolon (for
                        example 1, 5; 2, 6). A setting used in several places, such as a threshold repeated in a cancel rule, changes
                        everywhere at once.
                    </Callout>
                    <div className="space-y-3">
                        {parsed.map(({ target }) => (
                            <FormInput
                                key={target.key}
                                helpText={describeGridTarget(target)}
                                label={target.subject}
                                noMargin
                                placeholder={suggestGridValuesExample(toGridValue(target.current))}
                                value={inputs[target.key] ?? ''}
                                onChange={(event) => setInputs((current) => ({ ...current, [target.key]: event.target.value }))}
                            />
                        ))}
                    </div>
                    {previewQuery.error instanceof Error && previewIsCurrent && (
                        <Callout variant="error">{previewQuery.error.message}</Callout>
                    )}
                    {preview && (
                        <div className="space-y-2">
                            <p className="text-sm text-zinc-300">
                                {preview.childCount} combination{preview.childCount === 1 ? '' : 's'}: {preview.newChildCount} new
                                {preview.childCount > preview.newChildCount
                                    ? `, ${preview.childCount - preview.newChildCount} already in the study`
                                    : ''}
                                .
                            </p>
                            <ul className="max-h-64 space-y-1 overflow-auto rounded border border-zinc-800 p-2 text-sm">
                                {preview.children.map((child) => (
                                    <li
                                        key={child.variantKey}
                                        className={child.existingVariantId === null ? 'text-zinc-200' : 'text-zinc-500'}
                                    >
                                        {child.label}
                                        {child.existingVariantId !== null && <span className="ml-2 text-xs">(exists)</span>}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {previewAxes !== null && !previewIsCurrent && (
                        <p className="text-xs text-amber-300">The values changed since the preview; preview again before creating.</p>
                    )}
                </div>
            )}
        </Modal>
    );
};

export default VariantGridModal;
